import { Link } from 'react-router-dom'
import { ArrowRight, Loader2, Plug } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useLinkedRockSetup } from '@/pages/integrations/measurements/setup/useLinkedRockSetup'
import {
  FINISH_SETUP,
  ROCK_SETUP_BANNER_REGION,
  ROCK_SETUP_PENDING_MESSAGE,
} from '@/pages/integrations/measurements/setup/copy'
import { useActiveSetupProgress } from '@/pages/integrations/measurements/setup/onboarding/auto/useActiveSetupProgress'
import { gettingReady } from '@/pages/integrations/measurements/setup/onboarding/auto/progressCopy'
import { rockSetupPath } from '@/pages/onboarding/rock-setup/setupPath'
import { useSetupLinkState } from '@/pages/onboarding/rock-setup/useSetupLinkState'
import { isFramed } from '@/utils/framing'
import { assistAnchor } from '@/features/assist/anchors'

/**
 * App-shell banner: "Rock is linked but not set up yet — Finish setup",
 * into the guided setup (`/setup/rock/<id>`). Shown to administrators while
 * the linked Rock's `onboarding_pending` holds (ROCK-ONBOARDING-REWORK T4).
 * It can't be dismissed: it goes away on its own once setup is finished.
 * The guided setup lives outside the app shell, so it never shows there;
 * framed pages (an embed) never carry it.
 *
 * While a background setup run is active (25i) it reads "Getting things
 * ready — n of m done" instead, even once setup itself is marked finished.
 *
 * @returns {JSX.Element|null}
 */
export default function RockSetupBanner() {
  const { connection, isPending } = useLinkedRockSetup()
  if (!connection?.id || isFramed()) return null
  return <LinkedRockBanner connectionId={connection.id} isPending={isPending} />
}

/**
 * @param {Object} props
 * @param {string} props.connectionId - the linked Rock
 * @param {boolean} props.isPending - its guided setup isn't finished
 * @returns {JSX.Element|null}
 */
function LinkedRockBanner({ connectionId, isPending }) {
  const linkState = useSetupLinkState()
  const progress = useActiveSetupProgress()

  if (!isPending && !progress.isActive) return null
  const Icon = progress.isActive ? Loader2 : Plug

  return (
    <div
      role="region"
      aria-label={ROCK_SETUP_BANNER_REGION}
      className="border-b border-info-solid/30 bg-info-subtle px-4 py-2 text-info-ink md:px-6"
      data-testid="rock-setup-banner"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <Icon className={`size-4 shrink-0 ${progress.isActive ? 'animate-spin' : ''}`} aria-hidden="true" />
        <p className="min-w-0 flex-1 text-sm font-medium" aria-live="polite" data-testid="rock-setup-banner-message">
          {progress.isActive ? gettingReady(progress.done, progress.total) : ROCK_SETUP_PENDING_MESSAGE}
        </p>
        <Button asChild size="sm" variant="outline">
          <Link to={rockSetupPath(connectionId)} state={linkState} {...assistAnchor('integrations.setup.finish_banner')}>
            {FINISH_SETUP}
            <ArrowRight className="ml-1 size-4" aria-hidden="true" />
          </Link>
        </Button>
      </div>
    </div>
  )
}
